/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterViewInit, Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { type Command, type PromptConfigFull, DeleteAbstract, Status } from '@dm/core';
import { type DataTurboClient, type DataTurboShare } from 'app/common/types/mo';

import { DataTurboShareService } from '../data-turbo-share.service';
import { DataTurboShareStrService } from '../data-turbo-share-str.service';

@Component({
    selector: 'app-delete-data-turbo-share',
    templateUrl: './delete-data-turbo-share.component.html',
    styleUrls: ['./delete-data-turbo-share.component.scss'],
})
export class DeleteDataTurboShareComponent extends DeleteAbstract<DataTurboShare | DataTurboClient> implements AfterViewInit {
    @Input() isFromDetail = false;
    @Output() callBack: EventEmitter<DataTurboClient[]> = new EventEmitter();
    i18n = this.str.i18n;

    constructor(
        injector: Injector,
        private str: DataTurboShareStrService,
        private dataTurboService: DataTurboShareService,
    ) {
        super(injector);
    }

    initTable(): void {
        this.tableColumnDefs = [
            {
                field: 'sharePath',
                header: this.i18n.label.share_path,
                className: ['table-cell-text-pre'],
            },
            {
                field: 'ID',
                header: this.i18n.label.id,
            },
        ];
    }

    ngAfterViewInit(): void {
        this.closeArgs.actionType = 'delete';
        this.initTable();

        const promptConfs = this.prompt();
        if (!this.isFromDetail && promptConfs && promptConfs.length) {
            this.promptService.multi(promptConfs, () => {
                this.promptService.close();
                this.exec();
            }, () => {
                this.promptService.close();
                this.close();
            });
        } else {
            this.exec();
        }
    }

    prompt(): PromptConfigFull[] {
        const prompts: PromptConfigFull[] = [
            {
                id: 'prompt-delete-data-turbo-share-highRisk-deleteDataTurbo',
                canHidden: true,
                opType: 'warning',
                title: this.i18n.title.deleteShare,
                content: this.i18n.highRisk.deleteDataTurbo,
                templateRef: this.tableTemplate,
                width: 700,
            },
        ];

        return prompts;
    }

    buildCmds(): Command[] {
        const cmds: Command[] = this.dataList.map((data: DataTurboShare | DataTurboClient) => ({
            name: this.isFromDetail ? this.i18n.rest.deleteDataTurboClient : this.i18n.rest.deleteDataTurbo,
            nameParams: this.isFromDetail ?
                [data.userName] :
                [data.sharePath],
            action: this.isFromDetail ?
                this.dataTurboService.deletePermission(data.ID, data.userName) :
                this.dataTurboService.delete(data.ID, data.vstoreId),
        }));

        return cmds;
    }

    exec(): void {
        const cmds = this.buildCmds();

        this.batchService.exePrompt(cmds, {
            onClose: (results) => {
                this.closeArgs.refresh = results.some((a) => a.status === Status.success);
                this.close();
                this.callBack.emit();
            },
        });
    }
}
